import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { getGlobalApiClient } from '../utils/apiClient.js';

/**
 * NotificationContext provides global state management for notifications
 * Handles unread count updates and real-time synchronization between components
 */
const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Load unread notification count from API
   */
  const loadUnreadCount = useCallback(async () => {
    // Don't make API calls if user is not authenticated
    if (!isLoaded || !isSignedIn) {
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    try {
      setError(null);

      // Check if user is authenticated before making API call
      const token = await getToken();
      if (!token || token === 'null' || typeof token !== 'string' || token.trim() === '') {
        // User is not authenticated, don't make API call
        setUnreadCount(0);
        setLoading(false);
        return;
      }
      
      // Create API client inside function to avoid dependency issues
      const apiClient = getGlobalApiClient(getToken);
      const response = await apiClient.get('/api/notifications/unread-count');
      
      if (response.success) {
        setUnreadCount(response.unread_count || 0);
      } else {
        // Handle structured error responses
        if (response.error && response.error.code === 'AUTH_ERROR') {
          setError('Authentication required');
        } else if (response.error && response.error.code === 'RESOURCE_NOT_FOUND') {
          setError('User not found');
          setUnreadCount(0);
        } else {
          throw new Error(response.error?.message || response.message || 'Failed to load unread count');
        }
      }
    } catch (error) {
      console.error('Failed to load unread count:', error);
      
      // Handle different types of errors
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        setError('Network error');
      } else if (error.status === 401 || error.message.includes('401') || error.message.includes('unauthorized') || error.message.includes('Authentication required')) {
        // Don't set error for auth issues, just silently fail
        setUnreadCount(0);
        setError(null);
      } else if (error.message.includes('500')) {
        setError('Server error');
      } else {
        setError('Failed to load count');
      }
    } finally {
      setLoading(false);
    }
  }, [getToken, isLoaded, isSignedIn]); // Add authentication state dependencies

  /**
   * Mark a notification as read and update unread count
   */
  const markAsRead = useCallback(async (notificationId) => {
    // Don't make API calls if user is not authenticated
    if (!isLoaded || !isSignedIn) {
      return { success: false, error: 'Authentication required' };
    }

    try {
      // Check if user is authenticated before making API call
      const token = await getToken();
      if (!token || token === 'null' || typeof token !== 'string' || token.trim() === '') {
        return { success: false, error: 'Authentication required' };
      }
      
      const apiClient = getGlobalApiClient(getToken);
      const response = await apiClient.post(`/api/notifications/${notificationId}/read`);
      
      if (response.success || response.already_read) {
        // Decrease unread count by 1 if it was successfully marked as read
        if (response.success && !response.already_read) {
          setUnreadCount(prev => Math.max(0, prev - 1));
        }
        return { success: true, already_read: response.already_read };
      } else {
        return { success: false, error: response.error };
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
      return { success: false, error: error.message };
    }
  }, [getToken, isLoaded, isSignedIn]);

  /**
   * Refresh unread count (for manual refresh)
   */
  const refreshUnreadCount = useCallback(() => {
    loadUnreadCount();
  }, [loadUnreadCount]);

  /**
   * Manually update unread count (for optimistic updates)
   */
  const updateUnreadCount = useCallback((newCount) => {
    setUnreadCount(Math.max(0, newCount));
  }, []);

  /**
   * Decrease unread count by a specific amount
   */
  const decreaseUnreadCount = useCallback((amount = 1) => {
    setUnreadCount(prev => Math.max(0, prev - amount));
  }, []);

  // Load unread count only when user is authenticated
  useEffect(() => {
    if (isLoaded && isSignedIn) {
      loadUnreadCount();
    } else if (isLoaded && !isSignedIn) {
      // User is not signed in, reset state
      setUnreadCount(0);
      setLoading(false);
      setError(null);
    }
  }, [isLoaded, isSignedIn, loadUnreadCount]);

  const value = {
    unreadCount,
    loading,
    error,
    markAsRead,
    refreshUnreadCount,
    updateUnreadCount,
    decreaseUnreadCount,
    loadUnreadCount
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
